import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, GraduationCap, CheckCircle2, XCircle, Users2, AlertOctagon,
} from "lucide-react";
import { useVerificationQueue } from "../../lib/adminQueues";
import { useSelection } from "../../lib/useSelection";
import { SelectCheckbox } from "./SelectCheckbox";
import { BulkActionBar } from "./BulkActionBar";
import { BulkConfirmDialog } from "./BulkConfirmDialog";
import { SummaryList } from "./CampaignReviewTab";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export const VerificationTab = () => {
  const { items, loading, error, refresh, bulkVerify, bulkReject } = useVerificationQueue();
  const sel = useSelection();
  const [action, setAction] = useState(null); // null | "verify" | "reject"

  const ids = useMemo(() => items.map((s) => s.user_id), [items]);
  const selectedItems = items.filter((s) => sel.isSelected(s.user_id));

  const runVerify = async () => {
    const res = await bulkVerify(sel.ids);
    const p = res.processed || 0;
    toast.success(`${p} student${p === 1 ? "" : "s"} marked as enrollment-verified.`, {
      description: "Their campaigns can now be approved in the Campaign Review queue.",
    });
    sel.clear();
    setAction(null);
  };

  const runReject = async (note) => {
    const res = await bulkReject(sel.ids, note);
    const p = res.processed || 0;
    toast.success(`${p} student${p === 1 ? "" : "s"} sent back for more info.`);
    sel.clear();
    setAction(null);
  };

  return (
    <div data-testid="tab-verification">
      {error && (
        <p className="mb-6 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>
      )}

      {items.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-border bg-bone px-5 py-3">
          <label className="flex items-center gap-3 text-sm font-medium text-ink">
            <SelectCheckbox
              testid="verify-select-all"
              checked={sel.allSelected(ids)}
              indeterminate={sel.count > 0 && !sel.allSelected(ids)}
              onChange={(v) => (v ? sel.selectAll(ids) : sel.clear())}
              label="Select all students"
            />
            {sel.count === 0
              ? `Select all (${items.length})`
              : `${sel.count} of ${items.length} selected`}
          </label>
          <button
            type="button"
            onClick={refresh}
            data-testid="verify-refresh"
            className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink"
          >
            Refresh
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-olive">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading students…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="verify-empty"
          className="rounded-2xl border-2 border-dashed border-border bg-bone p-10 text-center"
        >
          <Users2 className="mx-auto h-9 w-9 text-olive" strokeWidth={1.5} />
          <h3 className="mt-3 font-serif text-2xl text-ink">Every student is verified.</h3>
          <p className="mt-1 text-sm text-olive">
            When a new student signs up, they'll show up here for a manual check until we wire the NSC API.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {items.map((s) => (
              <StudentRow
                key={s.user_id}
                s={s}
                checked={sel.isSelected(s.user_id)}
                onToggle={() => sel.toggle(s.user_id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <BulkActionBar count={sel.count} label="student" onClear={sel.clear}>
        <button
          type="button"
          onClick={() => setAction("verify")}
          data-testid="verify-bulk-verify"
          className="inline-flex items-center gap-1.5 rounded-full bg-yellow px-4 py-2 text-xs font-semibold text-ink transition-colors hover:brightness-95"
        >
          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
          Verify
        </button>
        <button
          type="button"
          onClick={() => setAction("reject")}
          data-testid="verify-bulk-reject"
          className="inline-flex items-center gap-1.5 rounded-full border border-sand/25 bg-sand/5 px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-sand/10"
        >
          <XCircle className="h-3.5 w-3.5" strokeWidth={2} />
          Send back
        </button>
      </BulkActionBar>

      <BulkConfirmDialog
        open={action === "verify"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Verify {sel.count} student{sel.count === 1 ? "" : "s"}?</>}
        description="Marks each student's enrollment as verified. Their pending campaigns become eligible for approval."
        itemsSummary={<SummaryList items={selectedItems.map((s) => ({
          left: s.display_name || s.email,
          right: s.school || s.edu_email || "—",
        }))} />}
        confirmLabel="Mark as verified"
        onConfirm={runVerify}
      />
      <BulkConfirmDialog
        open={action === "reject"}
        onOpenChange={(v) => !v && setAction(null)}
        title={<>Send {sel.count} back for more info?</>}
        description="Enrollment verification stays incomplete until they upload updated proof. Add a note so they know what's missing."
        itemsSummary={<SummaryList items={selectedItems.map((s) => ({
          left: s.display_name || s.email,
          right: s.school || s.edu_email || "—",
        }))} />}
        requireNote
        notePlaceholder="e.g. Your enrollment letter is dated last semester — please share the current-semester letter from your registrar."
        noteMinLength={8}
        confirmLabel="Send back with note"
        confirmTone="danger"
        onConfirm={runReject}
      />
    </div>
  );
};

const StudentRow = ({ s, checked, onToggle }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6, height: 0 }}
    transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
    className={`flex items-start gap-4 rounded-xl border-2 p-5 transition-colors ${
      checked ? "border-ink bg-yellow/5" : "border-ink/10 bg-bone hover:border-ink/25"
    }`}
    data-testid={`verify-row-${s.user_id}`}
  >
    <div className="pt-0.5">
      <SelectCheckbox
        checked={checked}
        onChange={onToggle}
        testid={`verify-checkbox-${s.user_id}`}
        label={`Select ${s.display_name || s.email}`}
      />
    </div>

    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tangerine/12 text-tangerine">
      <GraduationCap className="h-5 w-5" strokeWidth={1.75} />
    </div>

    <div className="min-w-0 flex-1">
      <p className="truncate font-serif text-lg text-ink" data-testid={`verify-name-${s.user_id}`}>
        {s.display_name || s.email}
      </p>
      <p className="mt-0.5 text-sm text-olive">
        {s.email}
        {s.school && <> · {s.school}</>}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {s.edu_verified ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-grass/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-grass">
            <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} /> .edu confirmed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-yellow/25 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink">
            .edu unconfirmed
          </span>
        )}
        {s.pending_campaign_id && (
          <span className="inline-flex items-center gap-1 rounded-full bg-cobalt/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-cobalt">
            Awaiting on: {s.pending_campaign_title || "Untitled campaign"}
          </span>
        )}
        {s.enrollment_rejection_note && (
          <span
            data-testid={`verify-note-pill-${s.user_id}`}
            className="inline-flex items-center gap-1 rounded-full bg-scarlet/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-scarlet"
          >
            <AlertOctagon className="h-3 w-3" strokeWidth={2.5} /> Sent back
          </span>
        )}
        <span className="text-[10px] text-olive">Joined {fmtDate(s.created_at)}</span>
      </div>
      {s.enrollment_rejection_note && (
        <blockquote
          data-testid={`verify-note-${s.user_id}`}
          className="mt-3 rounded-md border-l-4 border-scarlet bg-scarlet/[0.04] px-4 py-2 text-sm italic text-ink"
        >
          &ldquo;{s.enrollment_rejection_note}&rdquo;
        </blockquote>
      )}
    </div>
  </motion.article>
);
